import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, useInView, AnimatePresence } from "framer-motion";
import { Phone, ArrowRight, ArrowUpRight, MessageCircle, Wrench, Hammer, CircleDot } from "lucide-react";
import { api } from "../lib/api";
import { useI18n } from "../lib/i18n";
import KineticHeading from "../components/KineticHeading";
import Marquee from "../components/Marquee";
import { DEFAULT_PHOTOS, DEFAULT_HERO_PIVOT } from "../data/sitePhotos";

const WA_PHONE = "18196490166";

// Premium tool photography, packaged locally with the application.
const SPOTLIGHT = [
  { name_fr: "Remorque dompeur", name_en: "Dump trailer", cat_fr: "Remorques", cat_en: "Trailers", tag: "01", img: "https://images.pexels.com/photos/17133452/pexels-photo-17133452.png" },
  { name_fr: "Marteau piqueur Hilti", name_en: "Hilti breaker", cat_fr: "Outils électriques", cat_en: "Power tools", tag: "02", img: "https://images.pexels.com/photos/3822843/pexels-photo-3822843.jpeg" },
  { name_fr: "Scie Sawzall", name_en: "Sawzall", cat_fr: "Outils de coupe", cat_en: "Cutting tools", tag: "03", img: "https://images.pexels.com/photos/210881/pexels-photo-210881.jpeg" },
  { name_fr: "Échafaudages & échelles", name_en: "Scaffolding & ladders", cat_fr: "Élévation", cat_en: "Elevation", tag: "04", img: "https://images.pexels.com/photos/4491881/pexels-photo-4491881.jpeg" },
];

const CAT_ROWS = [
  { key: "electriques", num: "01" },
  { key: "coupe",       num: "02" },
  { key: "beton",       num: "03" },
  { key: "fixation",    num: "04" },
  { key: "echelles",    num: "05" },
  { key: "remorques",   num: "06" },
];

// ---------- little section chapter marker ----------
const ChapterLabel = ({ children, color = "#E87B3A" }) => (
  <div className="chapter-label" style={{ color }}>
    <span>{children}</span>
  </div>
);

// ---------- reveal wrapper ----------
const Reveal = ({ children, delay = 0, y = 40, className = "", once = true }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once, margin: "-15% 0px -10% 0px" });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.85, ease: [0.22, 1, 0.36, 1], delay }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

export default function Home() {
  const { t, lang } = useI18n();
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [photos, setPhotos] = useState(DEFAULT_PHOTOS);
  const [pivot, setPivot] = useState(DEFAULT_HERO_PIVOT);
  const [wheelCenter, setWheelCenter] = useState(null);

  const heroRef = useRef(null);
  const { scrollYProgress: heroProg } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const { scrollY } = useScroll();
  const gridY = useTransform(heroProg, [0, 1], [0, -120]);
  // Direct pixel mapping starts at the first scroll, before the hero clears the header.
  const wheelRot = useTransform(scrollY, (pixels) => pixels * 1.2);

  useEffect(() => {
    const hero = heroRef.current;
    const image = new Image();
    let active = true;
    const align = () => {
      if (!active || !image.naturalWidth) return;
      const photo = hero.querySelector('.hero-tool-photo');
      const [px, py] = getComputedStyle(photo).backgroundPosition.split(' ').map(parseFloat);
      const scale = Math.max(hero.clientWidth / image.naturalWidth, hero.clientHeight / image.naturalHeight);
      setWheelCenter({
        x: (hero.clientWidth - image.naturalWidth * scale) * px / 100 + image.naturalWidth * scale * pivot.x / 100,
        y: (hero.clientHeight - image.naturalHeight * scale) * py / 100 + image.naturalHeight * scale * pivot.y / 100,
      });
    };
    image.onload = align;
    image.src = photos.hero;
    const observer = new ResizeObserver(align);
    observer.observe(hero);
    window.addEventListener('resize', align);
    return () => { active = false; observer.disconnect(); window.removeEventListener('resize', align); };
  }, [photos.hero, pivot]);

  useEffect(() => {
    api.get("/items").then(({ data }) => setItems(data)).catch(() => setItems([]));
    api.get("/media").then(({ data }) => {
      setPhotos({ ...DEFAULT_PHOTOS, ...Object.fromEntries(Object.entries(data.photos || {}).filter(([, value]) => value)) });
      if (data.heroPivot) setPivot(data.heroPivot);
    }).catch(() => {});
    const t = setTimeout(() => setLoaded(true), 100);
    return () => clearTimeout(t);
  }, []);

  const waHref = `https://wa.me/${WA_PHONE}?text=${encodeURIComponent(t("wa.message"))}`;

  const marqueeItems = t("home.marquee") || [];
  const manifestoTitle = t("home.manifestoTitle") || [];
  const spotlightTitle = t("home.spotlightTitle") || [];
  const finalLines = t("home.final") || [];

  return (
    <main data-testid="home-page" className="bg-paper text-[#0F172A]">

      {/* ==================== HERO — CHAPTER 00 ==================== */}
      <section
        ref={heroRef}
        className="relative min-h-[100vh] overflow-hidden bg-ink text-white flex flex-col"
        data-testid="hero"
      >
        {/* blueprint grid — parallax drift */}
        <motion.div
          style={{ y: gridY }}
          className="absolute inset-0 bg-blueprint bp-drift opacity-70"
          aria-hidden
        />
        {/* grain */}
        <div className="absolute inset-0 bg-grain-dark opacity-70" aria-hidden />
        {/* deep radial glow */}
        <div
          className="absolute inset-0 opacity-70"
          style={{
            background:
              "radial-gradient(70% 55% at 82% 30%, rgba(232,123,58,0.28) 0%, rgba(232,123,58,0) 60%), radial-gradient(60% 55% at 10% 90%, rgba(26,78,114,0.6) 0%, rgba(26,78,114,0) 60%)",
          }}
          aria-hidden
        />
        {/* Tool photography with a dark left-side scrim for the existing copy. */}
        <div
          className="absolute inset-0 hero-tool-photo"
          style={{ backgroundImage: `url(${photos.hero})` }}
          aria-hidden
        />
        <div className="absolute inset-0 hero-tool-scrim" aria-hidden />

        {/* thin corner marks */}
        <div className="pointer-events-none absolute inset-0">
          <span className="absolute top-6 left-6 w-4 h-4 border-t border-l border-white/40" />
          <span className="absolute top-6 right-6 w-4 h-4 border-t border-r border-white/40" />
          <span className="absolute bottom-6 left-6 w-4 h-4 border-b border-l border-white/40" />
          <span className="absolute bottom-6 right-6 w-4 h-4 border-b border-r border-white/40" />
        </div>

        {/* Rotating 3D wheel emblem — parallax accent */}
        <div className="absolute w-[560px] h-[560px] md:w-[720px] md:h-[720px] pointer-events-none"
          style={{ left: wheelCenter?.x || 0, top: wheelCenter?.y || 0, transform: 'translate(-50%, -50%)', visibility: wheelCenter ? 'visible' : 'hidden' }} data-testid="hero-wheel-anchor">
        <motion.div
          style={{ rotate: wheelRot }}
          data-testid="hero-wheel"
          className="w-full h-full opacity-70"
          aria-hidden
        >
          <svg viewBox="0 0 200 200" className="w-full h-full">
            <defs>
              <radialGradient id="wg" cx="50%" cy="50%" r="50%">
                <stop offset="60%" stopColor="rgba(232,123,58,0)" />
                <stop offset="98%" stopColor="rgba(232,123,58,0.35)" />
              </radialGradient>
            </defs>
            <circle cx="100" cy="100" r="98" fill="none" stroke="url(#wg)" strokeWidth="0.6" />
            <circle cx="100" cy="100" r="88" fill="none" stroke="rgba(255,255,255,0.10)" strokeWidth="0.4" />
            <circle cx="100" cy="100" r="72" fill="none" stroke="rgba(232,123,58,0.55)" strokeWidth="0.3" strokeDasharray="1 3" />
            {Array.from({ length: 60 }).map((_, i) => (
              <line
                key={i}
                x1="100" y1="6" x2="100" y2="14"
                stroke="rgba(255,255,255,0.35)"
                strokeWidth="0.4"
                transform={`rotate(${i * 6} 100 100)`}
              />
            ))}
            <text x="100" y="102" textAnchor="middle" fill="rgba(232,123,58,0.9)" fontFamily="Bebas Neue" fontSize="10" letterSpacing="4">
              LOCATION RNC · QUÉBEC · MMXXVI ·
            </text>
          </svg>
        </motion.div>
        </div>

        {/* Top ticker strip */}
        <div className="relative z-10 border-b border-white/10">
          <div className="mx-auto max-w-[1440px] px-6 lg:px-12 py-3 flex items-center justify-between text-[10px] font-body font-bold uppercase tracking-[0.3em] text-white/60">
            <AnimatePresence>
              {loaded && (
                <>
                  <motion.span initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}>
                    N 46°22′ · W 75°45′
                  </motion.span>
                  <motion.span initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.45 }} className="hidden md:inline">
                    Édition Nord · Vol. 01
                  </motion.span>
                  <motion.span initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6 }} className="text-[#E87B3A]">
                    ● En service
                  </motion.span>
                </>
              )}
            </AnimatePresence>
          </div>
        </div>

        {/* Hero content */}
        <div className="relative z-10 flex-1 flex items-center">
          <div className="mx-auto max-w-[1440px] w-full px-6 lg:px-12 py-16 md:py-24 grid grid-cols-12 gap-6">
            <div className="col-span-12 lg:col-span-9">
              {loaded && (
                <>
                  <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.35, duration: 0.7 }}
                  >
                    <ChapterLabel color="#E87B3A">{t("home.chapter0")}</ChapterLabel>
                  </motion.div>

                  <h1 className="mt-6 font-display uppercase text-white leading-[0.85]">
                    <KineticHeading
                      className="text-[15vw] md:text-[10.5vw] lg:text-[9vw]"
                      lines={[t("hero.title1")]}
                      delay={0.35}
                    />
                    <KineticHeading
                      className="text-[15vw] md:text-[10.5vw] lg:text-[9vw]"
                      lineClassName="text-[#E87B3A] font-serif italic tracking-tight"
                      lines={[`${t("hero.title2_a")} ${t("hero.title2_b")}`]}
                      delay={0.55}
                    />
                  </h1>

                  <motion.p
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.9, duration: 0.7 }}
                    className="mt-8 font-body text-white/70 text-base md:text-lg max-w-xl leading-relaxed"
                  >
                    {t("hero.sub")}
                  </motion.p>

                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 1.05, duration: 0.7 }}
                    className="hero-actions mt-10 flex flex-col sm:flex-row gap-3"
                  >
                    <a
                      href="tel:8193503027"
                      data-testid="hero-call-cta"
                      className="hero-action hero-action-copper group inline-flex items-center justify-center gap-3 text-white pl-6 pr-4 py-4 font-body font-bold text-xs"
                    >
                      <Phone size={17} /> <span className="hero-action-label">{t("home.ctaCall")}</span>
                      <span className="w-8 h-8 -mr-1 rounded-full bg-white/15 flex items-center justify-center group-hover:translate-x-1 transition-transform">
                        <ArrowUpRight size={14} />
                      </span>
                    </a>
                    <a
                      href={waHref}
                      target="_blank" rel="noopener noreferrer"
                      data-testid="hero-wa-cta"
                      className="hero-action hero-action-whatsapp group inline-flex items-center justify-center gap-3 text-white px-6 py-4 font-body font-bold text-xs"
                    >
                      <MessageCircle size={18} /> <span className="hero-action-label">{t("home.ctaWa")}</span>
                    </a>
                    <Link
                      to="/catalogue"
                      data-testid="hero-catalog-cta"
                      className="hero-action hero-action-glass group inline-flex items-center justify-center gap-3 text-white px-6 py-4 font-body font-bold text-xs"
                    >
                      <span className="hero-action-label">{t("home.ctaCatalog")}</span> <ArrowRight size={17} className="group-hover:translate-x-1 transition-transform" />
                    </Link>
                  </motion.div>
                </>
              )}
            </div>

            <motion.aside
              initial={{ opacity: 0, x: 40 }}
              animate={loaded ? { opacity: 1, x: 0 } : {}}
              transition={{ delay: 1.2, duration: 0.9 }}
              className="hidden lg:flex col-span-3 flex-col justify-end gap-8"
            >
              <div className="border-l border-white/15 pl-5 space-y-5">
                <div>
                  <div className="font-display text-4xl text-[#E87B3A]">65+</div>
                  <div className="font-body text-[10px] font-bold uppercase tracking-[0.3em] text-white/50 mt-1">{t("hero.stat1")}</div>
                </div>
                <div>
                  <div className="font-display text-4xl text-[#E87B3A]">04</div>
                  <div className="font-body text-[10px] font-bold uppercase tracking-[0.3em] text-white/50 mt-1">{t("hero.stat2")}</div>
                </div>
                <div>
                  <div className="font-display text-4xl text-[#E87B3A]">7j / 24h</div>
                  <div className="font-body text-[10px] font-bold uppercase tracking-[0.3em] text-white/50 mt-1">{t("hero.stat3")}</div>
                </div>
              </div>
              <div className="text-[10px] font-body uppercase tracking-[0.3em] text-white/40">
                {t("home.loading")}
              </div>
            </motion.aside>
          </div>
        </div>

        {/* scroll cue */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: loaded ? 1 : 0 }}
          transition={{ delay: 1.4, duration: 0.6 }}
          className="relative z-10 border-t border-white/10"
        >
          <div className="mx-auto max-w-[1440px] px-6 lg:px-12 py-4 flex items-center justify-between text-[10px] font-body font-bold uppercase tracking-[0.3em] text-white/50">
            <span className="flex items-center gap-2">
              <CircleDot size={10} className="text-[#E87B3A]" /> Scroll · Explorer
            </span>
            <span>Phil · Ligne directe</span>
          </div>
        </motion.div>
      </section>

      {/* ==================== MARQUEE ==================== */}
      <section className="bg-[#0B1620] text-[#F5F1EA] border-y border-white/10 py-6 md:py-8">
        <Marquee
          items={marqueeItems}
          className="marquee-mask"
          itemClassName="text-[#F5F1EA]"
        />
      </section>

      {/* ==================== CHAPTER 01 — MANIFESTO ==================== */}
      <section className="relative bg-paper py-24 md:py-40 overflow-hidden">
        <div className="absolute inset-0 bg-grain opacity-70 pointer-events-none" />
        <div className="mx-auto max-w-[1440px] px-6 lg:px-12 relative">
          <div className="grid grid-cols-12 gap-8">
            <Reveal className="col-span-12 lg:col-span-5">
              <ChapterLabel color="#1A4E72">{t("home.chapter1")}</ChapterLabel>
              <div className="mt-6 font-body text-xs font-bold uppercase tracking-[0.3em] text-[#E87B3A]">
                {t("home.manifestoKicker")}
              </div>
            </Reveal>

            <div className="col-span-12 lg:col-span-7">
              <h2 className="font-display uppercase text-[#0F172A] text-6xl md:text-7xl lg:text-[6.5vw] leading-[0.92]">
                <Reveal delay={0.05}>{manifestoTitle[0]}</Reveal>
                <Reveal delay={0.15}>
                  <span className="font-serif italic text-[#1A4E72] tracking-tight">{manifestoTitle[1]}</span>
                </Reveal>
                <Reveal delay={0.25}>
                  <span className="text-[#E87B3A]">{manifestoTitle[2]}</span>
                </Reveal>
              </h2>

              <Reveal delay={0.35}>
                <p className="mt-10 font-body text-lg md:text-xl text-[#334155] max-w-2xl leading-[1.7]">
                  {t("home.manifestoBody")}
                </p>
              </Reveal>

              {/* 3 pillars */}
              <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-8 border-t border-[#0F172A]/15 pt-10">
                {[
                  { icon: Wrench, t: t("home.pillar1Title"), b: t("home.pillar1Body") },
                  { icon: CircleDot, t: t("home.pillar2Title"), b: t("home.pillar2Body") },
                  { icon: Hammer, t: t("home.pillar3Title"), b: t("home.pillar3Body") },
                ].map((p, i) => (
                  <Reveal key={i} delay={0.1 * i} className="group" >
                    <div className="flex items-start gap-4">
                      <div className="font-display text-3xl text-[#E87B3A] shrink-0 leading-none">0{i + 1}</div>
                      <div>
                        <div className="font-display text-2xl uppercase text-[#0F172A]">{p.t}</div>
                        <div className="font-body text-sm text-[#475569] mt-2 leading-relaxed">{p.b}</div>
                      </div>
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ==================== CHAPTER 02 — SPOTLIGHT ==================== */}
      <section className="relative bg-ink text-white py-24 md:py-40 overflow-hidden">
        <div className="absolute inset-0 bg-blueprint-dense opacity-40" />
        <div className="absolute inset-0 bg-grain-dark opacity-40" />
        <div className="relative mx-auto max-w-[1440px] px-6 lg:px-12">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16">
            <div>
              <ChapterLabel color="#E87B3A">{t("home.chapter2")}</ChapterLabel>
              <div className="mt-6 font-body text-xs font-bold uppercase tracking-[0.3em] text-[#E87B3A]/80">
                {t("home.spotlightKicker")}
              </div>
              <h2 className="mt-3 font-display uppercase text-white text-5xl md:text-7xl lg:text-[6vw] leading-[0.92]">
                <Reveal>{spotlightTitle[0]}</Reveal>
                <Reveal delay={0.1}>
                  <span className="font-serif italic text-[#E87B3A] tracking-tight">{spotlightTitle[1]}</span>
                </Reveal>
              </h2>
            </div>
            <Reveal delay={0.2}>
              <Link
                to="/catalogue"
                data-testid="home-view-all"
                className="inline-flex items-center gap-3 font-body text-xs font-bold uppercase tracking-[0.25em] text-white hover:text-[#E87B3A] transition-colors"
              >
                <span className="h-px w-10 bg-current" /> {t("top.viewAll")} <ArrowUpRight size={14} />
              </Link>
            </Reveal>
          </div>

          <div className="grid grid-cols-12 gap-6 md:gap-8">
            {SPOTLIGHT.map((s, i) => {
              const alt = i % 2 === 0;
              return (
                <Reveal key={s.tag} delay={0.05 * i} className={`col-span-12 sm:col-span-6 lg:col-span-6 ${alt ? "lg:pt-0" : "lg:pt-24"}`}>
                  <Link
                    to="/catalogue"
                    className="group block corner-bracket"
                    data-testid={`spotlight-${s.tag}`}
                  >
                    <div className="relative overflow-hidden bg-black aspect-[4/5]">
                      {/* spotlight vignette */}
                      <div className="absolute inset-0 z-10 bg-spotlight opacity-90 group-hover:opacity-60 transition-opacity duration-700" />
                      {/* image */}
                      <motion.img
                        src={photos[`spotlight${i + 1}`] || s.img}
                        alt={lang === "fr" ? s.name_fr : s.name_en}
                        className="absolute inset-0 w-full h-full object-cover"
                        initial={{ scale: 1.05 }}
                        whileHover={{ scale: 1.14 }}
                        transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1] }}
                      />
                      {/* clipped frame stripe */}
                      <div className="absolute inset-0 z-20 border border-white/10 pointer-events-none" />
                      {/* index tag */}
                      <div className="absolute top-4 left-4 z-30 font-display text-white text-2xl tracking-wider">
                        {s.tag}<span className="text-[#E87B3A]">.</span>
                      </div>
                      <div className="absolute bottom-6 left-6 right-6 z-30">
                        <div className="font-body text-[10px] font-bold uppercase tracking-[0.3em] text-[#E87B3A]">
                          {lang === "fr" ? s.cat_fr : s.cat_en}
                        </div>
                        <div className="mt-2 font-display text-3xl md:text-4xl uppercase text-white leading-tight flex items-end justify-between gap-4">
                          <span>{lang === "fr" ? s.name_fr : s.name_en}</span>
                          <ArrowUpRight size={26} className="text-[#E87B3A] shrink-0 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
                        </div>
                      </div>
                    </div>
                  </Link>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* ==================== CHAPTER 03 — INVENTORY GRID ==================== */}
      <section className="relative bg-paper py-24 md:py-32 overflow-hidden">
        <div className="absolute inset-0 bg-blueprint-light opacity-70" />
        <div className="relative mx-auto max-w-[1440px] px-6 lg:px-12 grid grid-cols-12 gap-8">
          <div className="col-span-12 lg:col-span-4">
            <Reveal>
              <ChapterLabel color="#1A4E72">{t("home.chapter3")}</ChapterLabel>
              <div className="mt-6 font-body text-xs font-bold uppercase tracking-[0.3em] text-[#E87B3A]">
                {t("cats.kicker")}
              </div>
              <h2 className="mt-3 font-display uppercase text-[#0F172A] text-5xl md:text-6xl leading-[0.95]">
                {t("cats.title1")}<br />
                <span className="font-serif italic text-[#1A4E72] tracking-tight">{t("cats.title2")}</span>
              </h2>
              <p className="mt-6 font-body text-[#475569] leading-relaxed max-w-md">
                {t("home.catsBody")}
              </p>
              <Link
                to="/catalogue"
                data-testid="categories-cta"
                className="mt-8 inline-flex items-center gap-2 bg-[#0F172A] hover:bg-[#E87B3A] text-white px-6 py-3 rounded-sm font-body font-bold text-xs uppercase tracking-[0.2em] transition-colors"
              >
                {t("cats.cta")} <ArrowRight size={14} />
              </Link>
            </Reveal>
          </div>

          <div className="col-span-12 lg:col-span-8">
            <div className="border-t border-[#0F172A]/15">
              {CAT_ROWS.map((c, i) => {
                const count = items.filter((it) => it.category === c.key).length;
                return (
                  <Reveal key={c.key} delay={0.05 * i}>
                    <Link
                      to="/catalogue"
                      data-testid={`cat-row-${c.key}`}
                      className="group flex items-center justify-between py-6 md:py-8 border-b border-[#0F172A]/15 hover:bg-[#0F172A] hover:text-white transition-colors px-2"
                    >
                      <div className="flex items-center gap-6 md:gap-10">
                        <span className="font-display text-2xl md:text-3xl text-[#E87B3A]">{c.num}</span>
                        <span className="font-display uppercase text-3xl md:text-5xl leading-none">
                          {t(`cats_labels.${c.key}`)}
                        </span>
                      </div>
                      <div className="flex items-center gap-5 md:gap-8">
                        <span className="hidden md:inline font-body text-xs font-bold uppercase tracking-[0.25em] text-[#475569] group-hover:text-white/60">
                          {count} {t("cats.count")}
                        </span>
                        <ArrowUpRight size={22} className="text-[#0F172A] group-hover:text-[#E87B3A] group-hover:translate-x-1 group-hover:-translate-y-1 transition-all" />
                      </div>
                    </Link>
                  </Reveal>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* ==================== CHAPTER 04 — FINAL CTA ==================== */}
      <section className="relative bg-[#E87B3A] text-white overflow-hidden">
        <div className="absolute inset-0 bg-stripes-heavy opacity-60" />
        <div className="absolute -top-10 -left-10 w-[420px] h-[420px] rounded-full bg-white/10 blur-3xl" />
        <div className="relative mx-auto max-w-[1440px] px-6 lg:px-12 py-24 md:py-40 grid grid-cols-12 gap-8">
          <div className="col-span-12 lg:col-span-7">
            <Reveal>
              <ChapterLabel color="rgba(255,255,255,0.75)">{t("home.chapter4")}</ChapterLabel>
            </Reveal>
            <h2 className="mt-8 font-display uppercase text-white text-6xl md:text-8xl lg:text-[8vw] leading-[0.88]">
              {finalLines.map((l, i) => (
                <Reveal key={i} delay={0.08 * i}>
                  <span className={i === 1 ? "font-serif italic text-white/90 tracking-tight block" : "block"}>{l}</span>
                </Reveal>
              ))}
            </h2>
          </div>
          <div className="col-span-12 lg:col-span-5 flex flex-col justify-end">
            <Reveal delay={0.2}>
              <div className="font-body text-sm font-bold uppercase tracking-[0.3em] text-white/85">
                {t("home.finalKicker")}
              </div>
              <p className="mt-5 font-body text-lg leading-relaxed text-white/95 max-w-md">
                {t("home.finalBody")}
              </p>
              <div className="mt-8 flex flex-col gap-3">
                <a
                  href="tel:8193503027"
                  data-testid="banner-call"
                  className="group inline-flex items-center justify-between gap-3 bg-[#0F172A] text-white pl-6 pr-3 py-5 rounded-sm font-body font-bold text-sm uppercase tracking-[0.2em] hover:bg-white hover:text-[#0F172A] transition-colors"
                >
                  <span className="flex items-center gap-3"><Phone size={16} /> {t("home.ctaCall")}</span>
                  <span className="w-10 h-10 flex items-center justify-center bg-[#E87B3A] text-white group-hover:translate-x-1 transition-transform">
                    <ArrowUpRight size={16} />
                  </span>
                </a>
                <a
                  href={waHref}
                  target="_blank" rel="noopener noreferrer"
                  data-testid="banner-wa"
                  className="group inline-flex items-center justify-between gap-3 bg-white text-[#0F172A] pl-6 pr-3 py-5 rounded-sm font-body font-bold text-sm uppercase tracking-[0.2em] hover:bg-[#25D366] hover:text-white transition-colors"
                >
                  <span className="flex items-center gap-3"><MessageCircle size={16} /> {t("home.ctaWa")}</span>
                  <span className="w-10 h-10 flex items-center justify-center bg-[#25D366] text-white group-hover:translate-x-1 transition-transform">
                    <ArrowUpRight size={16} />
                  </span>
                </a>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </main>
  );
}
